import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { responseHandler } from "@msuite/ui/utils";
import { type ReturnOrderFormSchema, returnOrderFormSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ReturnOrderForm } from "@/forms/return-order-form";
import { client } from "@/hono";

/** Props Interface */
interface AddReturnOrderModalProps extends ModalProps {
	onComplete?: () => Promise<unknown> | undefined;
	defaultValues?: Partial<ReturnOrderFormSchema>;
}

/**
 * Raise a return for a site being cleared.
 *
 * Submitting also prints the run's sheet — the page that lives in the truck and carries the QR the
 * warehouse scans when it arrives. That happens on the server, because the sheet is the run's
 * evidence rather than a convenience: without it a returning lorry is anonymous.
 */
export function AddReturnOrderModal({
	isOpen,
	onClose,
	onComplete,
	defaultValues,
}: AddReturnOrderModalProps) {
	/** Hooks */
	const { t } = useTranslation();
	const methods = useForm<ReturnOrderFormSchema>({
		resolver: resolver(returnOrderFormSchema),
		defaultValues: {
			note: null,
			vehicleIds: [],
			...defaultValues,
		},
	});

	/** Functions */
	async function handleOnSubmit(json: ReturnOrderFormSchema) {
		const response = await client.stock["return-orders"].$post({ json });
		await responseHandler(response);
		await onComplete?.();
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("stock.add_return_order")}
				isLoading={methods.formState.isLoading}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
			>
				<Modal.Body>
					<ReturnOrderForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
